import React, { useRef } from 'react';
import { Box, Button, Chip } from '@mui/material';
import { Typography, useI18nContext } from '@procore/core-react';
import AddIcon from '@mui/icons-material/Add';
import ClearIcon from '@mui/icons-material/Clear';
import RemoveIcon from '@mui/icons-material/Remove';
import { Filter, FilterState } from '../../helpers/filters';
import { useStyles } from './FiltersPills.styles';
import AnalyticsWrappedComponent from '../Analytics/AnalyticsWrappedComponent';
import useFilterPillItemChopper from './useFilterPillItemChopper';

interface FilterPillProps {
  item: Filter;
  updateCheckbox: Function;
}

export function FilterPill({ item, updateCheckbox }: FilterPillProps) {
  const classes = useStyles();
  return (
    <Chip
      className="filter-pill"
      role="group"
      tabIndex={-1}
      label={
        <Typography style={{ color: 'white', whiteSpace: 'normal' }}>
          {item.label}
        </Typography>
      }
      onDelete={item.immutable ? undefined : () => updateCheckbox(item)}
      color="primary"
      data-test-id={`filter-pill-${item.key}`}
      deleteIcon={
        <Button className={classes.deleteIcon}>
          <ClearIcon
            className={classes.deleteIcon}
            titleAccess={`Remove ${item.label} filter`}
          />
        </Button>
      }
      key={item.label}
      disabled={item.immutable}
      style={{ height: '100%' }}
    />
  );
}

interface FiltersPillsProps {
  loading: boolean;
  useFilterHandlers: FilterState;
  showClearFilters?: boolean;
}

export default function FiltersPills({
  loading,
  useFilterHandlers,
  showClearFilters = true,
}: FiltersPillsProps) {
  const I18n = useI18nContext();
  const classes = useStyles();
  const { filters, updateCheckbox, clearFilters } = useFilterHandlers;
  const pills = [
    ...filters.constructionSector,
    ...filters.companyType,
    ...filters.classifications,
    ...filters.trade,
  ].sort((a, b) => a.label.localeCompare(b.label));
  const containerRef = useRef(null);
  const pillsLength = pills.length;

  const { chopIndex, loaded, showMore, setShowMore } = useFilterPillItemChopper(
    pillsLength,
    containerRef
  );

  return (
    <Box
      data-testid="chipContainer"
      mt={1}
      visibility={!loaded ? 'hidden' : {}}
      display="flex"
      alignItems="center"
    >
      <div ref={containerRef}>
        {pills.slice(0, chopIndex).map((i) => (
          <FilterPill key={i.key} item={i} updateCheckbox={updateCheckbox} />
        ))}
        {pills.length > 0 &&
          !loading &&
          !showMore &&
          chopIndex !== pillsLength && (
            <Box
              data-test-id="show-more"
              className={classes.showMoreOrLess}
              onClick={() => {
                setShowMore(true);
              }}
            >
              <AddIcon className={classes.showMoreOrLessIcon} />
              <Typography intent="body" weight="semibold">
                {I18n.t('components.filters.show_more', {
                  amount: pillsLength - chopIndex,
                })}
              </Typography>
            </Box>
          )}
        {!loading && showMore && (
          <Box
            data-test-id="show-less"
            className={classes.showMoreOrLess}
            onClick={() => {
              setShowMore(false);
            }}
          >
            <RemoveIcon className={classes.showMoreOrLessIcon} />
            <Typography intent="body" weight="semibold">
              {I18n.t('components.filters.show_less')}
            </Typography>
          </Box>
        )}
        {showClearFilters && pills.filter((f) => !f.immutable).length > 0 && (
          <AnalyticsWrappedComponent
            data-test-id="clear-filters"
            componentType={Typography}
            eventText="Clicked Clear Filters Link"
            eventLabel="Filters"
            component="span"
            className={classes.clearFilters}
            onClick={() => {
              clearFilters();
            }}
            onKeyDown={(e) => {
              if (
                [13, 32].includes(e.keyCode ?? e.which) &&
                document.activeElement instanceof HTMLElement
              ) {
                e.preventDefault();
                document.activeElement.click();
              }
            }}
            tabIndex={0}
            intent="body"
            weight="semibold"
          >
            {I18n.t('components.filters.clear_all_filters')}
          </AnalyticsWrappedComponent>
        )}
      </div>
    </Box>
  );
}
